import { useCallback, useState, useRef, useEffect } from "react";
import Map, { Source, Layer, NavigationControl } from "react-map-gl";
import {
  mdMapConfig,
  paMapConfig,
  vaMapConfig,
  mapStyle,
  mapboxToken,
} from "../../config.js";
import { filterDistricts } from "../../utils";
import { mapStyles } from "../../config.js";
import states from "../../data/states.js";
import mea from "../../data/mea.js";
import * as layers from "../../layers.js";
import {
  AppFilters,
  Copyright,
  MapFiltersButton,
  StateMapButtons,
  DistrictDetailPanel,
  Icon,
  Title
} from "./styled.js";
import { Wrapper, Container, MapSearch } from "../App/styled.js";
import DistrictPopup from "../DistrictPopup";

function MEAMap({ className, stateCode, stateName, ...props }) {
  const isMobile = window.matchMedia("(max-width: 640px)").matches;
  let config;
  switch (stateCode) {
    case "MD":
      config = mdMapConfig;
      break;
    case "PA":
      config = paMapConfig;
      break;
    case "VA":
      config = vaMapConfig;
      break;
    default:
      break;
  }
  const mapConfig = isMobile
    ? { ...mdMapConfig, zoom: config.mobileZoom }
    : config;

  const [activeDistrict, setActiveDistrict] = useState("");
  const [activeHoverDistrict, setActiveHoverDistrict] = useState("");
  const [hoverDistrict, setHoverDistrict] = useState(null);
  const [district, setDistrict] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [stateDistricts, setStateDistricts] = useState(null);
  const [mapViewState, setMapViewState] = useState(mapConfig);
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState({
    aoi: [],
    decarbonizingTechnology: [],
    awardYear: [],
  });

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [districtDetailOpen, setDistrictDetailOpen] = useState(false);
  const mapRef = useRef();

  const isAwardDistrict = useCallback((districtName) => {
    return districts?.features.some((district) => {
      return district.properties.name === districtName;
    })
  }, [districts]);

  useEffect(() => {
    fetch(`/${stateCode.toLowerCase()}-data.json`)
      .then((response) => {
        if (!response.ok) {
          throw new Error("HTTP error " + response.status);
        }
        return response.json();
      })
      .then((json) => {
        setStateDistricts(json);
      })
      .catch(function (error) {
        console.log(error);
      });
  }, [stateCode]);

  const handleReset = () => {
    setMapViewState(mapConfig);
    setDistrict(null);
  };

  const onMove = useCallback(
    ({ viewState }) => {
      if (viewState.zoom < mapConfig.minZoom) {
        viewState.zoom = mapConfig.minZoom;
      }
      setMapViewState(viewState);
    },
    [mapConfig.minZoom]
  );

  const onClick = useCallback(
    (event) => {
      event.originalEvent.stopPropagation();
      const feature = event.features && event.features[0];
      switch (feature?.layer.id) {
        case "state-district-hover":
          if (feature.properties.longitude && feature.properties.latitude) {
            const targetDistrict =
              activeDistrict === feature.properties.name
                ? null
                : feature.properties;
            const targetDistrictName = targetDistrict
              ? feature.properties.name
              : "";
            const toggleState = targetDistrict ? true : false;
            setDistrict(targetDistrict);
            setActiveDistrict(targetDistrictName);
            setDistrictDetailOpen(toggleState);
            setFiltersOpen(false);
          }
          break;
        case "state-fills":
          resetDistrict();
          setFiltersOpen(false);
          break;
        default:
          resetDistrict();
          setFiltersOpen(false);
          break;
      }
    },
    [activeDistrict]
  );

  const onHover = useCallback((event) => {
    const feature = event.features && event.features[0];

    if (
      feature?.layer.id === "state-district-fills" ||
      feature?.layer.id === "state-district-fills-mea" ||
      feature?.layer.id === "state-district-hover"
    ) {
      setActiveHoverDistrict(feature.properties.school_dis);
      setHoverDistrict(feature.properties);
      if(isAwardDistrict(feature.properties.name)){
        mapRef.current.getCanvas().style.cursor = "pointer";
      } else {
        mapRef.current.getCanvas().style.cursor = "";
      }
    } else {
      mapRef.current.getCanvas().style.cursor = "";
      setActiveHoverDistrict("");
      setHoverDistrict(null);
    }
    if (!feature) {
      setActiveHoverDistrict("");
      setHoverDistrict(null);
    }
  }, [isAwardDistrict]);

  const onMouseLeave = useCallback((event) => {
    setActiveHoverDistrict("");
  }, []);

  const handleToggle = () => {
    setFiltersOpen(!filtersOpen);
  };

  const handleDistrictDetailToggle = () => {
    resetDistrict();
  };

  const resetDistrict = () => {
    setDistrict(null);
    setActiveDistrict("");
    setDistrictDetailOpen(false);
  };

  const handleFilter = (filters) => {
    setActiveFilters(filters);
  };

  const handleDistrictSearch = (searchTerm) => {
    setActiveSearchTerm(searchTerm);
  };

  useEffect(() => {
    setDistricts(
      filterDistricts(activeSearchTerm, activeFilters, stateDistricts, mea)
    );
    resetDistrict();
  }, [activeSearchTerm, activeFilters, stateDistricts]);

  return (
    <>
      <MapFiltersButton
        activeFilters={activeFilters}
        results={districts?.features}
        onClick={handleToggle}
      />
      <Wrapper>
        <Title>Maryland Energy Authority’s Decarbonizing Public Schools Program</Title>
        <MapSearch
          placeholder={"School District Name"}
          onSearch={(searchTerm) => {
            handleDistrictSearch(searchTerm);
          }}
        />
        <Container>
          <AppFilters
            activeFilters={activeFilters}
            results={districts?.features}
            isOpen={filtersOpen}
            onToggle={handleToggle}
            onFilter={handleFilter}
            type="awards"
            position="left"
          />
          <DistrictDetailPanel
            isOpen={districtDetailOpen}
            onToggle={handleDistrictDetailToggle}
            district={district}
            mea={mea}
          />
          <Map
            ref={mapRef}
            {...mapViewState}
            id="statesMap"
            onMove={onMove}
            width="100%"
            height="100%"
            mapStyle={mapStyle}
            mapboxAccessToken={mapboxToken}
            onClick={onClick}
            onMouseMove={onHover}
            onMouseLeave={onMouseLeave}
            interactiveLayerIds={[
              "state-fills",
              "state-district-hover",
              "state-district-fills",
              "state-district-fills-mea",
            ]}
          >
            <NavigationControl showCompass={false} />
            <Source id="states" type="geojson" data={states}>
              <Layer
                id="state-fills"
                type="fill"
                paint={{ "fill-color": "#fff", "fill-opacity": 1 }}
              />
            </Source>
            <Source id="all-district-data" type="geojson" data={stateDistricts}>
              <Layer {...layers.stateDistrictFills} />
            </Source>
            <Source id="district-data" type="geojson" data={districts}>
              <Layer {...layers.stateDistrictFillsMEA} />
               <Layer
                id="state-district-hover"
                type="fill"
                paint={{
                  "fill-color": mapStyles.legendTier5Color,
                  "fill-opacity": 1,
                }}
                filter={["==", "school_dis", activeHoverDistrict]}
              />
              <Layer
                id="state-district-active"
                type="fill"
                paint={{
                  "fill-color": mapStyles.legendTier6Color,
                  "fill-opacity": 1,
                }}
                filter={["==", "school_dis", activeDistrict]}
              />
            </Source>
            <Source id="all-district-data" type="geojson" data={stateDistricts}>
              <Layer {...layers.stateAllDistrictBorders} />
              <Layer
                {...layers.stateActiveDistrictBorder}
                filter={["==", "school_dis", activeDistrict]}
              />
            </Source>
            {hoverDistrict && (
              <DistrictPopup
                anchor="bottom"
                longitude={Number(hoverDistrict.longitude)}
                latitude={Number(hoverDistrict.latitude)}
                closeOnClick={false}
                closeButton={false}
                data={hoverDistrict}
                showDetails={false}
              ></DistrictPopup>
            )}
          </Map>
          <StateMapButtons
            mapRef={mapRef}
            onReset={handleReset}
            showReset={true}
          />
          <Copyright>
            <Icon src="icon.png" alt="" />Map Produced by Generation180
          </Copyright>
        </Container>
      </Wrapper>
    </>
  );
}

export default MEAMap;
